import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core'
import SydButton from '../../../../../commonDesign/Button'
import SydInput from '../../../../../commonDesign/SydInput'

const MIN_W = '0'

const useStyles = makeStyles((theme) => ({
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '0.8125rem',
    '& th': {
      textAlign: 'left',
      padding: '6px 8px',
      borderBottom: 'var(--border-neutral-thin)',
      fontWeight: 600,
      whiteSpace: 'nowrap'
    },
    '& td': {
      padding: '4px 8px',
      borderBottom: 'var(--border-neutral-thin)',
      verticalAlign: 'middle'
    }
  },
  columnChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '2px 8px',
    border: 'var(--border-neutral-thin)',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: '0.75rem',
    marginRight: '4px',
    marginBottom: '4px',
    cursor: 'pointer',
    background: 'none',
    font: 'inherit',
    color: 'inherit',
    '&.__selected': {
      background: 'var(--color-primary-100)',
      borderColor: 'var(--color-primary-500)'
    }
  },
  footer: {
    marginTop: theme.layout.gap.g5
  }
}))

function IndexesEditor ({ indexes, columns, onChange }) {
  const classes = useStyles()
  const columnNames = columns.map(c => c.columnName).filter(Boolean)

  const updateIndex = useCallback((idx, field, value) => {
    const next = indexes.map((ix, i) => {
      if (i !== idx) return ix
      return { ...ix, [field]: value }
    })
    onChange(next)
  }, [indexes, onChange])

  const toggleIndexColumn = useCallback((idxPos, colName) => {
    const ix = indexes[idxPos]
    const currentCols = ix.columns || []
    const hasCol = currentCols.some(c => c.name === colName)
    let nextCols
    if (hasCol) {
      nextCols = currentCols.filter(c => c.name !== colName)
    } else {
      nextCols = [...currentCols, { name: colName, ordinalPosition: currentCols.length + 1 }]
    }
    nextCols = nextCols.map((c, i) => ({ ...c, ordinalPosition: i + 1 }))
    updateIndex(idxPos, 'columns', nextCols)
  }, [indexes, updateIndex])

  const addIndex = useCallback(() => {
    onChange([
      ...indexes,
      {
        _id: Math.random().toString(36).slice(2),
        indexName: 'unique_key',
        isUnique: true,
        columns: []
      }
    ])
  }, [indexes, onChange])

  const removeIndex = useCallback((idx) => {
    onChange(indexes.filter((_, i) => i !== idx))
  }, [indexes, onChange])

  return (
    <div>
      <table className={classes.table}>
        <thead>
          <tr>
            <th>Index Name</th>
            <th>Unique</th>
            <th>Columns</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {indexes.map((ix, idx) => (
            <tr key={ix._id || idx}>
              <td>
                <SydInput
                  minWidth={MIN_W}
                  value={ix.indexName}
                  onChange={(e) => updateIndex(idx, 'indexName', e.target.value)}
                  size='sm'
                  placeholder='index_name'
                />
              </td>
              <td>
                <input
                  type='checkbox'
                  checked={ix.isUnique}
                  onChange={(e) => updateIndex(idx, 'isUnique', e.target.checked)}
                />
              </td>
              <td>
                <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                  {columnNames.map(colName => {
                    const selected = (ix.columns || []).some(c => c.name === colName)
                    return (
                      <button
                        type='button'
                        key={colName}
                        className={`${classes.columnChip} ${selected ? '__selected' : ''}`}
                        onClick={() => toggleIndexColumn(idx, colName)}
                        aria-pressed={selected}
                      >
                        {colName}
                      </button>
                    )
                  })}
                  {columnNames.length === 0 && (
                    <span style={{ color: 'var(--color-text-secondary)', fontSize: '0.75rem' }}>
                      Define columns first
                    </span>
                  )}
                </div>
              </td>
              <td>
                <SydButton variant='outline' size='sm' onClick={() => removeIndex(idx)}>
                  &#10005;
                </SydButton>
              </td>
            </tr>
          ))}
          {indexes.length === 0 && (
            <tr>
              <td colSpan={4} style={{ textAlign: 'center', padding: '16px', color: 'var(--color-text-secondary)' }}>
                No indexes defined.
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <div className={classes.footer}>
        <SydButton variant='outline' size='sm' onClick={addIndex}>
          Add Index
        </SydButton>
      </div>
    </div>
  )
}

IndexesEditor.propTypes = {
  indexes: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired
}

export default IndexesEditor
